import { FvcDataPoint } from "../../misc/api/apiTypes";

interface FirstCycle {
    cycleDirection: CycleDirection;
    cycleIndex: number;
}

enum CycleDirection {
    notSet = 0,
    goingPositive = 1,
    goingNegative = 2
}

const CrossOverDuration = 0.15;
const StartOfTestDuration = 0.1;
const StartOfTestFlow = 0.1;
const TransitionThreshold = 0.15;
const TidalTransitionThreshold = 0.1;

export function extractFirstCycle(flowData: FvcDataPoint[]): FirstCycle {
    let positiveCycleCounter = 0.0;
    let negativeCycleCounter = 0.0;

    const sampleRate = Math.abs(flowData[0].Time - flowData[1].Time);
    const crossOverIndex = CrossOverDuration / sampleRate;
    const result: FirstCycle = { cycleDirection: CycleDirection.notSet, cycleIndex: 0 };
    let lastVolume = flowData[0].Volume;

    for (let index = 1; index < flowData.length; index++) {
        const fvcFlowVolumeSample = flowData[index];

        if (fvcFlowVolumeSample.Volume > lastVolume) {
            if ((positiveCycleCounter += sampleRate) >= CrossOverDuration) {
                result.cycleDirection = CycleDirection.goingPositive;
                result.cycleIndex = index - crossOverIndex;
                break;
            }
        } else {
            if (fvcFlowVolumeSample.Volume < lastVolume) {
                if ((negativeCycleCounter += sampleRate) >= CrossOverDuration) {
                    result.cycleDirection = CycleDirection.goingNegative;
                    result.cycleIndex = index - crossOverIndex;
                }
            } else {
                positiveCycleCounter = 0;
                negativeCycleCounter = 0;
            }

            lastVolume = fvcFlowVolumeSample.Volume;
        }
    }

    return result;
}

export interface ExpiratoryCycle {
    startOfTestIndex: number;
    endOfTestIndex: number;
    numberOfCycles: number;
    isMultiBreath: boolean;
    samples: FvcDataPoint[];
}

function isExpiratoryTransition(fvcFlowVolumeSample: FvcDataPoint, lastVolume: number) {
    return fvcFlowVolumeSample.Volume > lastVolume && fvcFlowVolumeSample.Flow >= TransitionThreshold;
}

function isExpiratoryTransitionTidal(fvcFlowVolumeSample: FvcDataPoint, lastVolume: number) {
    return fvcFlowVolumeSample.Volume < lastVolume && fvcFlowVolumeSample.Flow >= TidalTransitionThreshold;
}

function isInspiratoryTransition(fvcFlowVolumeSample: FvcDataPoint, lastVolume: number) {
    return fvcFlowVolumeSample.Volume < lastVolume && fvcFlowVolumeSample.Flow <= -1 * TransitionThreshold;
}

function isInspiratoryTransitionTidal(fvcFlowVolumeSample: FvcDataPoint, lastVolume: number) {
    return fvcFlowVolumeSample.Volume > lastVolume && fvcFlowVolumeSample.Flow <= -1 * TidalTransitionThreshold;
}

function identifyExpiratoryCycle(rawData: FvcDataPoint[], sampleRate: number, firstCycle: FirstCycle) {
    let lastVolume = 0;
    let positiveVolume = 0;
    let negativeVolume = 0;
    let maxVolume = 0;
    let cycleDuration = 0;
    let rawDataIndex: number;
    let startIndex = 0;
    const result: ExpiratoryCycle = { startOfTestIndex: 0, endOfTestIndex: 0, numberOfCycles: 0, isMultiBreath: false, samples: [] };
    const crossOverIndex = CrossOverDuration / sampleRate;

    let currentStage = firstCycle.cycleDirection;

    for (rawDataIndex = firstCycle.cycleIndex; rawDataIndex < rawData.length; rawDataIndex++) {
        const fvcFlowVolumeSample = rawData[rawDataIndex];

        if (currentStage === CycleDirection.goingPositive) {
            if (isInspiratoryTransition(fvcFlowVolumeSample, lastVolume)) {
                if ((cycleDuration += sampleRate) >= CrossOverDuration) {
                    if ((positiveVolume - negativeVolume) > maxVolume) {
                        result.endOfTestIndex = rawDataIndex - crossOverIndex;
                        result.startOfTestIndex = startIndex;
                        maxVolume = positiveVolume - negativeVolume;
                    }

                    cycleDuration = 0;
                    currentStage = CycleDirection.goingNegative;
                    result.numberOfCycles++;
                }
            } else {
                positiveVolume = fvcFlowVolumeSample.Volume;
                cycleDuration = 0;
            }
        } else {
            if (isExpiratoryTransition(fvcFlowVolumeSample, lastVolume)) {
                if ((cycleDuration += sampleRate) >= CrossOverDuration) {
                    startIndex = rawDataIndex - crossOverIndex;
                    cycleDuration = 0;
                    currentStage = CycleDirection.goingPositive;
                }
            } else {
                negativeVolume = fvcFlowVolumeSample.Volume;
                cycleDuration = 0;
            }

            
        }

        lastVolume = fvcFlowVolumeSample.Volume;
    }

    if (currentStage === CycleDirection.goingNegative) { return result; }

    if (positiveVolume - negativeVolume > maxVolume) {
        result.endOfTestIndex = rawDataIndex - 1;
        result.startOfTestIndex = startIndex;
        result.numberOfCycles++;
    }

    return result;
}

function maxBy<T>(collection: T[], selector: (value: T) => number): T {
    return collection.reduce((a, b) => selector(a) >= selector(b) ? a : b, {} as T);
}

function minBy<T>(collection: T[], selector: (value: T) => number): T {
    return collection.reduce((a, b) => selector(a) <= selector(b) ? a : b, {} as T);
}

function identifyStartIndexOfExpiratory(rawData: FvcDataPoint[], sampleRate: number, cycle: ExpiratoryCycle) {
    const startTextIndex = StartOfTestDuration / sampleRate;

    const startExpIndex = cycle.startOfTestIndex;
    const endExpIndex = cycle.endOfTestIndex;
    const expiratoryCycle = rawData.slice(startExpIndex, endExpIndex - 1);

    let duration = 0;
    for (let i = 0; i < expiratoryCycle.length; i++) {
        const fvcFlowVolumeSample = expiratoryCycle[i];
        if (fvcFlowVolumeSample.Flow > StartOfTestFlow) {
            if (duration >= StartOfTestDuration) {
                return rawData.indexOf(expiratoryCycle[i - startTextIndex]);
            }

            duration += sampleRate;
        } else {
            duration = 0;
        }
    }

    const maxPef = maxBy<FvcDataPoint>(expiratoryCycle, e => e.Flow);
    const maxPefIndex = expiratoryCycle.indexOf(maxPef);

    for (let i = maxPefIndex; i > 0; i--) {
        const sample = expiratoryCycle[i];
        if (sample.Flow <= 0) {
            return rawData.indexOf(sample);
        }
    }

    return startExpIndex;
}

function identifyEndIndexOfExpiratory(cycle: ExpiratoryCycle) {
    return cycle.endOfTestIndex;
}

export function extractExpiratoryCurveData(rawData: FvcDataPoint[], firstCycle: FirstCycle): ExpiratoryCycle {
    const expiratorySamples: FvcDataPoint[] = [];
    const sampleRate = Math.abs(rawData[0].Time - rawData[1].Time);
    const proposedCycleInfo = identifyExpiratoryCycle(rawData, sampleRate, firstCycle);

    if (proposedCycleInfo.numberOfCycles === 0)
        throw Error("No expiratory detected.");

    const startOfExpTestRawIndex = identifyStartIndexOfExpiratory(rawData, sampleRate, proposedCycleInfo);
    const endOfExpTestRawIndex = identifyEndIndexOfExpiratory(proposedCycleInfo);

    const expiratory = rawData.slice(startOfExpTestRawIndex, endOfExpTestRawIndex - 1);
    let time = 0;
    const minVolSample = minBy<FvcDataPoint>(expiratory, e => e.Volume);

    const zeroVolume = minVolSample.Time === 0 ? 0 : minVolSample.Volume;

    for (const fvcFlowVolumeSample of expiratory) {
        expiratorySamples.push({
            Flow: fvcFlowVolumeSample.Flow,
            Volume: fvcFlowVolumeSample.Volume - zeroVolume,
            Time: +(time).toFixed(2),
        });
        time = +(time + sampleRate).toFixed(2);
    }

    return {
        startOfTestIndex: startOfExpTestRawIndex,
        endOfTestIndex: endOfExpTestRawIndex,
        numberOfCycles: proposedCycleInfo.numberOfCycles,
        isMultiBreath: proposedCycleInfo.numberOfCycles > 2,
        samples: expiratorySamples
    }
}

interface TidalCycle {
    startOfTestIndex: number;
    endOfTestIndex: number;
    samples: FvcDataPoint[];
}

export function identifyTidalCycles(rawData: FvcDataPoint[], sampleRate: number, cycle: ExpiratoryCycle) {
    const tidalCrossOverDuration = 0.05;

    const tidals = [];

    let currentStage = CycleDirection.goingNegative;
    let lastVolume = 0;
    let cycleDuration = 0;
    const crossOverIndex = tidalCrossOverDuration / sampleRate;
    const referencePt = cycle.startOfTestIndex - 1;
    let endOfTidalIndex = referencePt;

    for (let rawDataIndex = referencePt; rawDataIndex >= 0; rawDataIndex--) {
        const fvcFlowVolumeSample = rawData[rawDataIndex];

        if (currentStage === CycleDirection.goingNegative) {
            if (isExpiratoryTransitionTidal(fvcFlowVolumeSample, lastVolume)) {
                if ((cycleDuration += sampleRate) >= tidalCrossOverDuration) {
                    cycleDuration = 0;
                    currentStage = CycleDirection.goingPositive;
                }
            } else {
                cycleDuration = 0;
            }
        } else {
            if (isInspiratoryTransitionTidal(fvcFlowVolumeSample, lastVolume)) {
                if ((cycleDuration += sampleRate) >= tidalCrossOverDuration) {
                    cycleDuration = 0;
                    currentStage = CycleDirection.goingNegative;

                    tidals.push({
                        startOfTestIndex: rawDataIndex + crossOverIndex,
                        endOfTestIndex: endOfTidalIndex
                    });

                    endOfTidalIndex = rawDataIndex + crossOverIndex - 1;
                }
            } else {
                cycleDuration = 0;
            }
        }

        lastVolume = fvcFlowVolumeSample.Volume;
    }
    return tidals;
}

export function extractTidalCurveData(rawData: FvcDataPoint[], cycle: ExpiratoryCycle): TidalCycle[] {
    if (!cycle.isMultiBreath)
        return [{ startOfTestIndex: 0, endOfTestIndex: 0, samples: [] }];

    const sampleRate = Math.abs(rawData[0].Time - rawData[1].Time);
    const tidalCycles = identifyTidalCycles(rawData, sampleRate, cycle);
    let newTime = 0;
    let minVolume = 9999;

    for (const tidalCycle of tidalCycles) {
        const tidalSection = rawData.slice(tidalCycle.startOfTestIndex, tidalCycle.endOfTestIndex - 1);

        if (minBy<FvcDataPoint>(tidalSection, s => s.Volume).Volume < minVolume)
            minVolume = minBy<FvcDataPoint>(tidalSection, s => s.Volume).Volume;

        for (const spirometrySample of tidalSection) {
            spirometrySample.Volume = spirometrySample.Volume - minVolume;
            spirometrySample.Time = newTime;
            newTime += sampleRate;
        }

        tidalCycle.samples = tidalSection;
    }

    return tidalCycles;
}

export function tZeroSample(cycle: ExpiratoryCycle): number {
    const sampleAtPef = maxBy(cycle.samples, s => s.Flow);

    const extrapolatedTimeMilliseconds = ((sampleAtPef.Time * 1000) - ((sampleAtPef.Volume / sampleAtPef.Flow) * 1000));
    const tZeroSample = cycle.samples.find(s => s.Time === +(extrapolatedTimeMilliseconds / 1000).toFixed(2));
    return cycle.samples.indexOf(tZeroSample);
}

export function adjustTimeToTZero(rawData: FvcDataPoint[], cycle: ExpiratoryCycle) {
    const tZeroIndex = tZeroSample(cycle);
    const sampleRate = Math.abs(rawData[0].Time - rawData[1].Time);

    const new0Index = cycle.startOfTestIndex + tZeroIndex;
    return rawData.map(rd => ({
        Volume: rd.Volume,
        Flow: rd.Flow,
        Time: +(rd.Time - (new0Index * sampleRate)).toFixed(2)
    }));


}
